import { useInfiniteQuery } from '@tanstack/react-query'
import type { Address } from 'ox'

import type { ActivityEntry, ActivityItem } from '../../worker/lib/activity.js'
import * as Account from './account.js'
import { api } from './api.js'
import { getSupportedChainId } from './chain.js'
import * as Zone from './zone.js'

type ApiError = Error & { status?: number | undefined }

/** Returns the React Query key for account activity. */
export function queryKey(account: string | null | undefined, options: queryKey.Options = {}) {
  const { chainId, zone } = options
  if (typeof chainId !== 'number') return Account.queryKey(account, 'activity')
  return Account.queryKey(account, 'activity', chainId, { zone: !!zone })
}

/** `queryKey` types. */
export declare namespace queryKey {
  /** Query options. */
  type Options = {
    chainId?: number | undefined
    zone?: boolean | undefined
  }
}

/** Returns the connected address's activity feed. */
export function useActivity(options: useActivity.Options) {
  const { account, chainId, enabled = true } = options
  const chainId_supported = getSupportedChainId(chainId)

  const zoneToken = Zone.useAuthToken()
  const authToken = zoneToken.data ?? null

  return useInfiniteQuery({
    queryKey: queryKey(account, { chainId: chainId_supported, zone: !!authToken }),
    queryFn: async ({ pageParam }) => {
      const query: Record<string, string> = {
        address: account!,
        chainId: String(chainId_supported),
      }
      if (pageParam) query.cursor = pageParam
      const res = await api.api.activity.$get(
        { query },
        {
          headers: authToken ? { Authorization: `Bearer ${authToken}` } : undefined,
        },
      )
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as { error?: string } | null
        const error = new Error(body?.error ?? 'Failed to load activity') as ApiError
        error.status = res.status
        throw error
      }
      return (await res.json()) as useActivity.Page
    },
    initialPageParam: undefined as string | undefined,
    enabled: enabled && Boolean(account),
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
  })
}

export declare namespace useActivity {
  /** Re-export for backward compatibility. */
  type Item = ActivityItem

  /** Paginated activity response. */
  type Page = {
    items: ActivityEntry[]
    nextCursor: string | null
  }

  /** Hook options. */
  type Options = {
    account: Address.Address | null
    chainId: number
    enabled?: boolean | undefined
  }
}
